// Service worker, notificaciones push (FCM) e instalación de la PWA.

import { useEffect, useState } from "react";
import { deleteToken, getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import { deleteDoc, doc, serverTimestamp, setDoc } from "firebase/firestore";
import { app, db } from "./firebase";
import { claveVapid, usaEmulador } from "./config";

let registro: Promise<ServiceWorkerRegistration | null> | null = null;

export function registrarServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!registro) {
    registro =
      "serviceWorker" in navigator && (import.meta.env.PROD || usaEmulador)
        ? navigator.serviceWorker.register("/sw.js").catch((e) => {
            console.warn("No se pudo registrar el service worker", e);
            return null;
          })
        : Promise.resolve(null);
  }
  return registro;
}

export type EstadoPush = "sin-soporte" | "ios-instalar" | "pendiente" | "activas" | "bloqueadas";

export const esIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
export const estaInstalada = () =>
  window.matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;

export async function estadoPush(): Promise<EstadoPush> {
  // En iPhone las notificaciones web solo funcionan con la app agregada a la pantalla de inicio.
  if (esIOS() && !estaInstalada()) return "ios-instalar";
  if (!("Notification" in window) || !(await isSupported())) return "sin-soporte";
  if (Notification.permission === "denied") return "bloqueadas";
  return Notification.permission === "granted" ? "activas" : "pendiente";
}

/** Pide permiso (si hace falta) y guarda el token de este celular en usuarios/{uid}/tokens. */
export async function activarPush(uid: string): Promise<EstadoPush> {
  const estado = await estadoPush();
  if (estado === "sin-soporte" || estado === "ios-instalar" || estado === "bloqueadas") return estado;
  if (Notification.permission !== "granted" && (await Notification.requestPermission()) !== "granted") return "bloqueadas";
  if (usaEmulador || !claveVapid) return "activas";
  const sw = await registrarServiceWorker();
  if (!sw) return "sin-soporte";
  const token = await getToken(getMessaging(app), { vapidKey: claveVapid, serviceWorkerRegistration: sw });
  await setDoc(doc(db, `usuarios/${uid}/tokens/${token}`), {
    plataforma: esIOS() ? "ios" : /Android/.test(navigator.userAgent) ? "android" : "web",
    actualizadoEn: serverTimestamp(),
  });
  return "activas";
}

/** Rechaza si la promesa tarda más de `ms`: sin señal, Firestore y FCM pueden quedarse esperando para siempre. */
function conLimite<T>(promesa: Promise<T>, ms: number): Promise<T> {
  return Promise.race([promesa, new Promise<never>((_, rechazar) => setTimeout(() => rechazar(new Error("Tiempo agotado")), ms))]);
}

/**
 * Al cerrar sesión: este celular deja de recibir los avisos de la cuenta. El token de FCM es del navegador,
 * no de la persona; si no se borra, quien entre después recibe también los avisos de la cuenta anterior.
 * Borra usuarios/{uid}/tokens/{token} (debe ir antes de signOut) y anula el token en FCM.
 */
export async function desactivarPush(uid: string): Promise<void> {
  if (usaEmulador || !claveVapid || !("Notification" in window) || Notification.permission !== "granted" || !(await isSupported())) return;
  const sw = await registrarServiceWorker();
  if (!sw) return;
  const mensajeria = getMessaging(app);
  const token = await conLimite(getToken(mensajeria, { vapidKey: claveVapid, serviceWorkerRegistration: sw }), 5000);
  await conLimite(deleteDoc(doc(db, `usuarios/${uid}/tokens/${token}`)), 5000).catch((e) => console.warn("No se pudo borrar el token", e));
  await conLimite(deleteToken(mensajeria), 5000);
}

/** Con la app abierta, FCM no muestra la notificación: la mostramos nosotros. */
export async function escucharPushEnPrimerPlano(alLlegar: (titulo: string, cuerpo: string) => void) {
  if (usaEmulador || !(await isSupported())) return () => {};
  return onMessage(getMessaging(app), (m) => alLlegar(m.data?.titulo ?? "", m.data?.cuerpo ?? ""));
}

interface EventoInstalar extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let eventoInstalar: EventoInstalar | null = null;
const oyentes = new Set<() => void>();
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  eventoInstalar = e as EventoInstalar;
  oyentes.forEach((f) => f());
});

/** Botón "Instalar app" en Android/Chrome. En iPhone se muestran instrucciones. */
export function useInstalar() {
  const [, refrescar] = useState(0);
  useEffect(() => {
    const f = () => refrescar((n) => n + 1);
    oyentes.add(f);
    return () => void oyentes.delete(f);
  }, []);
  return {
    puede: !!eventoInstalar && !estaInstalada(),
    instalar: async () => {
      if (!eventoInstalar) return;
      await eventoInstalar.prompt();
      await eventoInstalar.userChoice;
      eventoInstalar = null;
      oyentes.forEach((f) => f());
    },
  };
}
